import { Checkbox, Skeleton } from '@mantine/core';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import classes from './StepperCard.module.css';
import type { SlideOption } from '#plugin-chat/components/tools/ask-user-question/schema.ts';
import { useStreamingSuspense } from '#plugin-chat/lib/ui/streaming-suspense.tsx';

export interface CheckboxSlideProps {
  options: SlideOption[];
  selectedIds: string[];
  onOptionClick: (values: string[]) => void;
  isStreaming?: boolean;
}

/**
 * Skeleton fallback for a streaming checkbox option.
 */
function CheckboxSkeleton() {
  return <Skeleton className={classes.checkboxCard} height={60} radius="sm" />;
}

interface CheckboxOptionProps {
  option: SlideOption;
  isStreaming?: boolean;
}

/**
 * Single checkbox option that suspends while streaming.
 * Wrapped in Suspense by parent.
 */
function CheckboxOption({ option, isStreaming = false }: CheckboxOptionProps) {
  // Suspend if streaming and option doesn't have required fields
  useStreamingSuspense(option, isStreaming, ['id', 'label']);

  return (
    <Checkbox.Card className={classes.checkboxCard} value={option.id}>
      <div className={classes.checkboxContent}>
        <div className={classes.checkboxIndicator}>
          <Checkbox.Indicator />
        </div>
        <div className={classes.checkboxLabel}>{option.label}</div>
        <div className={classes.checkboxDescription}>{option.description}</div>
      </div>
    </Checkbox.Card>
  );
}

/** Chip-based multi-select slide with checkbox group. */
export function CheckboxSlide({ options, selectedIds, onOptionClick, isStreaming }: CheckboxSlideProps) {
  return (
    <Checkbox.Group className={classes.checkboxGroup} onChange={onOptionClick} value={selectedIds}>
      {options?.map((option, index) => (
        // Use option.id when available (stable identity), fall back to index for streaming
        <ErrorBoundary
          fallback={<CheckboxSkeleton />}
          key={option.id ?? `streaming-${index}`}
          onError={(error) => console.error('Checkbox option render error:', error)}
        >
          <Suspense fallback={<CheckboxSkeleton />}>
            <CheckboxOption isStreaming={isStreaming} option={option} />
          </Suspense>
        </ErrorBoundary>
      ))}
      {/* Show extra skeleton at the end while streaming to hint more options may come */}
      {isStreaming && <CheckboxSkeleton />}
    </Checkbox.Group>
  );
}
